import { createIsomorphicFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

/**
 * Host-based locale resolution.
 *
 * smartsustainability.ai IS the English site: it serves English at "/" and at
 * unprefixed section paths (/ideas, /concepts/attention-mining, ...). The old
 * "/en/*" URLs are legacy and 301 to their unprefixed equivalent.
 */
export const EN_HOSTS = new Set([
  "smartsustainability.ai",
  "www.smartsustainability.ai",
]);

export function isEnHostname(host?: string | null): boolean {
  if (!host) return false;
  return EN_HOSTS.has(host.toLowerCase().split(":")[0]);
}

/**
 * Turkish-brand production hosts. The English tree (/en/*) must not be
 * reachable here — it duplicates smartsustainability.ai — so those requests
 * are 301'd to the English domain. Preview/localhost hosts are excluded on
 * purpose so the editor preview can still browse both trees.
 */
export const TR_CANONICAL_HOSTS = new Set([
  "akillisurdurulebilirlik.com",
  "www.akillisurdurulebilirlik.com",
  "ardaoztaskin.com",
  "www.ardaoztaskin.com",
]);

export function isTrCanonicalHostname(host?: string | null): boolean {
  if (!host) return false;
  return TR_CANONICAL_HOSTS.has(host.toLowerCase().split(":")[0]);
}

export const EN_SITE_ORIGIN = "https://smartsustainability.ai";

/**
 * Legacy "/en/..." pathname -> its current unprefixed English path.
 * "/en" is the English homepage; renamed pages are mapped explicitly.
 */
const EN_LEGACY_RENAMES: Record<string, string> = {
  "/en/privacy": "/privacy-policy",
  "/en/cop31": "/cop31-guide",
};

export function stripEnPrefix(pathname: string): string {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/en") return "/";
  if (EN_LEGACY_RENAMES[p]) return EN_LEGACY_RENAMES[p];
  for (const [from, to] of Object.entries(EN_LEGACY_RENAMES)) {
    if (p.startsWith(from + "/")) return to + p.slice(from.length);
  }
  return p.startsWith("/en/") ? p.slice(3) : p;
}

/**
 * Absolute English-domain URL a legacy "/en/..." (or English section) path
 * should 301 to.
 */
export function enDomainTarget(pathname: string, search = ""): string {
  return `${EN_SITE_ORIGIN}${stripEnPrefix(pathname)}${search}`;
}

/** True when the current request/browser is on the English domain. */
export const detectEnHost = createIsomorphicFn()
  .client(() => isEnHostname(window.location.hostname))
  .server(() => {
    try {
      const req = getRequest();
      const host =
        req?.headers.get("x-forwarded-host") ?? req?.headers.get("host") ?? null;
      return isEnHostname(host);
    } catch {
      return false;
    }
  });

// head() is synchronous and TanStack does not expose route context/loaderData
// to it, so beforeLoad records the resolved host locale here for the root
// head() to read during the same render pass.
let lastEnHost = false;

export function rememberEnHost(value: boolean): boolean {
  lastEnHost = value;
  return value;
}

export function rememberedEnHost(): boolean {
  return lastEnHost;
}

/**
 * Legacy English section roots that duplicate a canonical English URL.
 * They already canonicalise to the right-hand path, so requests 301 there.
 */
const EN_DUPLICATE_ROOTS: Record<string, string> = {
  "/archive": "/ideas",
  "/reports": "/research",
  "/glossary": "/concepts",
};

/** Canonical English path a duplicate English URL should 301 to, if any. */
export function enDuplicateTarget(pathname: string): string | null {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const root = "/" + clean.split("/")[1];
  const target = EN_DUPLICATE_ROOTS[root];
  if (!target) return null;
  const rest = clean.slice(root.length);
  return `${target}${rest}`;
}
